import React from "react";
import { Controller, get, useFormContext } from "react-hook-form";
import DocumentField from "../../DocumentField";
import CreateDocumentForm from "../../CreateDocumentForm";
import CreateDocumentClassFormBody from "../CreateDocumentClassFormBody";
import { TempDocumentDetailResponseWithAny } from "features/document/hooks/queries/useTempDocumentDetailQuery";
import { CORP_CARD_USAGE_PROOF_FIELD as FIELD } from "config/documentConstant";
import DatePicker from "../../../../../foundation/DatePicker";
import CheckBox from "../../../../../foundation/CheckBox/CheckBox";
import styled from "styled-components";
import formatMinMaxLengthMessage from "../../../../../../utils/formatUtils/formatMinMaxLengthMessage";
import DocumentTextAreaField from "../../../../../foundation/DocumentTextField/DocumentTextArea";
import DocumentTextField from "../../../../../foundation/DocumentTextField/DocumentTextField";

/* 지출결의서 */
interface CorpCardUsageProofForm {
  [FIELD.USAGE_DATE.key]: string;
  [FIELD.USAGE_DETAILS.key]: {
    description: string;
    amount: string;
    paymentPlace: string;
    paymentAccount: string;
  }[];
  [FIELD.USAGE_TYPE.key]: string[];
  [FIELD.USAGE_REASON.key]: string;
  [FIELD.USAGE_CLIENTS.key]: {
    clientsDescription: string;
    number: string;
  };
}

interface CorpCardUsageProofProps {
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
}

const CorpCardUsageProof = ({
  tempDocumentDetail,
}: CorpCardUsageProofProps) => {
  const {
    control,
    setValue,
    register,
    formState: { errors },
  } = useFormContext<CorpCardUsageProofForm>();

  return (
    <CreateDocumentClassFormBody noBorderTop>
      <CreateDocumentForm.Field label={FIELD.USAGE_DATE.label}>
        <Controller
          name={FIELD.USAGE_DATE.key}
          control={control}
          rules={{
            required: "사용일자 기입 필수",
          }}
          render={({ field }) => (
            <DatePicker
              {...field}
              usage="document"
              placeholder="사용일자를 선택해 주세요."
              defaultValue={
                tempDocumentDetail?.documentData?.[FIELD.USAGE_DATE.key]
              }
              dateFormat={FIELD.USAGE_DATE.dateFormat}
              onChangeDate={(_, dateString) => {
                field.onChange(dateString);
              }}
              errorMessage={errors[FIELD.USAGE_DATE.key]?.message}
            />
          )}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.USAGE_DETAILS.label}>
        <DocumentField
          fieldMetaInfo={FIELD.USAGE_DETAILS as any}
          sumFieldName="amount"
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.USAGE_TYPE.label}>
        <CheckboxGroupStyle>
          <div>
            {FIELD.USAGE_TYPE.options.map((option) => {
              return (
                <Controller
                  key={option}
                  name={FIELD.USAGE_TYPE.key}
                  control={control}
                  rules={{
                    validate: (value) =>
                      (Array.isArray(value) && value.length > 0) ||
                      "증빙 사유 유형 기입 필수",
                  }}
                  render={({ field }) => {
                    // 기존 단일 문자열로 저장된 문서 호환: string/array 모두 수용
                    const current: string[] = Array.isArray(field.value)
                      ? field.value
                      : field.value
                        ? [field.value]
                        : [];
                    return (
                      <CheckBox
                        value={option}
                        checked={current.includes(option)}
                        onCheckedChange={() => {
                          const next = current.includes(option)
                            ? current.filter((v) => v !== option)
                            : [...current, option];
                          setValue(FIELD.USAGE_TYPE.key, next, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                      >
                        {option}
                      </CheckBox>
                    );
                  }}
                />
              );
            })}
          </div>

          {get(errors, FIELD.USAGE_TYPE.key)?.message && (
            <label>{get(errors, FIELD.USAGE_TYPE.key)?.message}</label>
          )}
        </CheckboxGroupStyle>
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.USAGE_REASON.label}>
        <Controller
          name={FIELD.USAGE_REASON.key}
          control={control}
          rules={{
            required: "사용 목적 기입 필수",
          }}
          render={() => (
            <DocumentTextAreaField
              placeholder="사용목적을 입력해 주세요."
              {...register(FIELD.USAGE_REASON.key, {
                minLength: {
                  value: FIELD.USAGE_REASON.minLength,
                  message: formatMinMaxLengthMessage(
                    FIELD.USAGE_REASON.minLength,
                    FIELD.USAGE_REASON.maxLength,
                  ),
                },
                maxLength: {
                  value: FIELD.USAGE_REASON.maxLength,
                  message: formatMinMaxLengthMessage(
                    FIELD.USAGE_REASON.minLength,
                    FIELD.USAGE_REASON.maxLength,
                  ),
                },
              })}
              errorMessage={errors[FIELD.USAGE_REASON.key]?.message}
            />
          )}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field
        label={
          <>
            (해당 시)
            <br />
            접대 대상자
          </>
        }
      >
        {FIELD.USAGE_CLIENTS.itemFields.map((itemField, i) => {
          return (
            <CreateDocumentForm.Field
              key={itemField.key}
              label={itemField.label}
            >
              <DocumentTextField
                placeholder={itemField.placeholder}
                style={{
                  marginTop: i === 1 ? "2rem" : undefined,
                }}
                {...register(`usageClients.${itemField.key}`, {
                  // 선택 항목이므로, 값이 있을 때만 길이 체크
                  validate: (value: string) => {
                    if (!value || value.length === 0) {
                      // 아무것도 안 쓴 건 허용
                      return true;
                    }

                    const min = itemField.minLength ?? 0;
                    const max = itemField.maxLength ?? Infinity;

                    if (value.length < min || value.length > max) {
                      return formatMinMaxLengthMessage(min, max);
                    }

                    return true;
                  },
                })}
                errorMessage={
                  errors.usageClients?.[
                    itemField.key as "clientsDescription" | "number"
                  ]?.message as string | undefined
                }
              />
            </CreateDocumentForm.Field>
          );
        })}
      </CreateDocumentForm.Field>
    </CreateDocumentClassFormBody>
  );
};

/* 필드 스타일  */
const CheckboxGroupStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  & > div {
    display: flex;
    gap: 2.4rem;
  }

  & > label {
    padding-left: 1.6rem;
    ${({ theme }) => theme.typography.body3.regular};
    color: ${({ theme }) => theme.color.text.danger};
  }
`;

export default CorpCardUsageProof;
